import { useCallback, useRef, useState } from "react";
import { http } from "@/lib/api";
import { micConstraints, createMeter, pickMime, SPEECH_LEVEL } from "@/lib/mic";

const SEGMENT_MS = 45000;
const SILENCE_WARN_MS = 6000;

export const useRecorder = (onTranscript, onError) => {
  const [recordingIndex, setRecordingIndex] = useState(null);
  const [pending, setPending] = useState({});
  const [level, setLevel] = useState(0);
  const [silent, setSilent] = useState(false);
  const session = useRef(null);
  const inflight = useRef([]);
  const chain = useRef({});

  const transcribe = useCallback(async (blob, index) => {
    const fd = new FormData();
    fd.append("audio", blob, `answer-${index}.${blob.type.includes("mp4") ? "m4a" : "webm"}`);
    return (await http.post("/transcribe", fd, { timeout: 120000 })).data.text || "";
  }, []);

  // Segments upload in parallel but are appended in recording order.
  const queue = useCallback((blob, index, heard) => {
    setPending((p) => ({ ...p, [index]: (p[index] || 0) + 1 }));
    const req = transcribe(blob, index).catch(() => transcribe(blob, index)).catch(() => null);
    const step = (chain.current[index] || Promise.resolve()).then(() => req).then((text) => {
      if (text) onTranscript(index, text);
      else if (text === null) onError("Transcription failed. You can type your answer instead.");
      else if (heard) onError("Didn't catch that. No words were recognised; try again or type your answer.");
    }).finally(() => setPending((p) => ({ ...p, [index]: p[index] - 1 })));
    chain.current[index] = step;
    inflight.current.push(step);
  }, [transcribe, onTranscript, onError]);

  const startSegment = useCallback((s) => {
    const mime = pickMime();
    const rec = new MediaRecorder(s.stream, mime ? { mimeType: mime } : undefined);
    const chunks = [];
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    const done = new Promise((resolve) => {
      rec.onstop = () => {
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        if (blob.size > 2000) queue(blob, s.index, s.heard);
        if (s.finalising) setPending((p) => ({ ...p, [s.index]: p[s.index] - 1 }));
        resolve();
      };
    });
    s.rec = rec;
    s.done = done;
    rec.start(1000);
  }, [queue]);

  const stop = useCallback(() => {
    const s = session.current;
    session.current = null;
    setRecordingIndex(null);
    setLevel(0);
    setSilent(false);
    if (!s) return Promise.resolve();
    clearInterval(s.timer);
    if (!s.heard && Date.now() - s.t0 > 2500) onError("No sound reached the app from your microphone. It may be muted or the wrong device. Use Test microphone to check.");
    if (s.rec.state !== "inactive") {
      s.finalising = true;
      setPending((p) => ({ ...p, [s.index]: (p[s.index] || 0) + 1 }));
      s.rec.stop();
    }
    return s.done.then(() => { s.meter.close(); s.stream.getTracks().forEach((t) => t.stop()); });
  }, [onError]);

  const start = useCallback(async (index) => {
    await stop();
    try {
      const stream = await navigator.mediaDevices.getUserMedia(micConstraints());
      const s = { index, stream, meter: createMeter(stream), heard: false, t0: Date.now(), segT0: Date.now() };
      session.current = s;
      startSegment(s);
      s.timer = setInterval(() => {
        const rms = s.meter.read();
        if (rms > SPEECH_LEVEL) s.heard = true;
        setLevel(Math.min(1, rms / (SPEECH_LEVEL * 4)));
        setSilent(!s.heard && Date.now() - s.t0 > SILENCE_WARN_MS);
        if (Date.now() - s.segT0 > SEGMENT_MS && s.rec.state === "recording") {
          s.segT0 = Date.now();
          s.rec.stop();
          startSegment(s);
        }
      }, 100);
      setRecordingIndex(index);
    } catch (e) {
      onError(e?.name === "NotAllowedError" ? "Microphone blocked. Allow mic access in the address bar, or type your answer." : "Microphone unavailable. Check it's connected and not used by another app, or type your answer.");
    }
  }, [startSegment, stop, onError]);

  const flush = useCallback(async () => {
    await stop();
    await Promise.all(inflight.current);
  }, [stop]);

  return { recordingIndex, pending, level, silent, start, stop, flush };
};
